// src/Utils/useEditableEntity.js
import { useCallback, useMemo, useRef, useState } from "react";

/**
 * useEditableEntity
 * -----------------
 * Yleistetty "editable entity" -hook:
 * - pitää baseRef (viimeisin ladattu/tallennettu totuus)
 * - draft state
 * - dirty derivointina (computeDirty)
 * - safe sync: ei ylikirjoita käyttäjän muokkauksia (unless force)
 * - save wrapper: estää turhat save:t, hallitsee saving-tilan ja päivittää baseRef
 *
 * Parametrit:
 * - initialDraft: objekti
 * - normalizeEntityToDraft(entity): draft-objekti
 * - computeDirty(draft, baseDraft): boolean
 * - canSyncRef?: ref boolean (esim typingRef tai "allowSyncRef")
 * - onSave(patch, ctx): async, tekee backend save’n ja palauttaa optional payload:
 *     {
 *       savedAt?: number,
 *       savedDraft?: object,  // jos backend muokkaa/normalisoi jotain
 *       mergedBase?: object,  // jos haluat pakottaa baseRef tiettyyn muotoon
 *     }
 * - buildPatch(draft, baseDraft): patch-objekti (default: kaikki kentät)
 */
export default function useEditableEntity({
  initialDraft,
  normalizeEntityToDraft,
  computeDirty,
  onSave,
  buildPatch,
  canSyncRef,
}) {
  const [draft, setDraft] = useState(initialDraft);
  const [saving, setSaving] = useState(false);

  // baseRef = viimeisin ladattu/tallennettu "totuus" (draft-muodossa)
  const baseRef = useRef(null);

  // dirty derivointina: ei pidetä erillisenä totuutena, vaan lasketaan draft vs base
  const dirty = useMemo(() => {
    const base = baseRef.current;
    return computeDirty(draft, base);
  }, [draft, computeDirty]);

  const setField = useCallback((key, value) => {
    setDraft((prev) => (prev?.[key] === value ? prev : { ...prev, [key]: value }));
  }, []);

  const setMany = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setDraft((prev) => ({ ...(prev || {}), ...patch }));
  }, []);

  /**
   * Aseta baseRef + draft suoraan entiteetistä (kun avaat detaljin tai vaihdat id:tä).
   * force=true ylikirjoittaa draftin aina.
   */
  const resetFromEntity = useCallback(
    (entity, { force = true } = {}) => {
      const next = normalizeEntityToDraft(entity);
      baseRef.current = next ? { ...next } : null;

      if (force) {
        setDraft(next || initialDraft);
      } else {
        // jos ei force, niin vain jos draft ei ole dirty
        if (!computeDirty(draft, baseRef.current)) {
          setDraft(next || initialDraft);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [normalizeEntityToDraft, initialDraft]
  );

  /**
   * Turvallinen synkronointi (esim detail fetchin jälkeen):
   * - jos käyttäjä ei ole muokkaamassa (dirty=false)
   * - JA canSyncRef ei estä (esim typingRef.current === false)
   */
  const syncFromEntityIfSafe = useCallback(
    (entity) => {
      const next = normalizeEntityToDraft(entity);
      if (!next) return;

      // jos ulkoinen ref estää synkin (esim typingRef)
      if (canSyncRef?.current === false) return;

      // jos käyttäjä on muokannut, älä ylikirjoita draftia
      const base = baseRef.current;
      const isDirtyNow = computeDirty(draft, base);

      // Päivitä baseRef aina kun data tulee – MUTTA vain jos se koskee samaa entityä,
      // käytännössä päätät sen kutsujassa (esim id match). Tässä ei oleteta id-kenttää.
      baseRef.current = { ...next };

      if (!isDirtyNow) {
        setDraft({ ...(draft || {}), ...next });
      }
    },
    [normalizeEntityToDraft, computeDirty, draft, canSyncRef]
  );

  /**
   * Päivitä baseRef "tallennetuksi" ilman refetchiä (optimistinen).
   * Hyvä kun save onnistuu ja haluat pitää draftin ja basen linjassa.
   */
  const commitBase = useCallback((nextBaseDraft) => {
    baseRef.current = nextBaseDraft ? { ...nextBaseDraft } : null;
  }, []);

  /**
   * Save wrapper:
   * - jos !dirty => no-op (palauttaa { skipped:true })
   * - buildPatch(draft, base) => patch
   * - onSave(patch, ctx)
   * - commit baseRef + päivitä draft (haluttaessa)
   */
  const save = useCallback(
    async (ctx = {}) => {
      const base = baseRef.current;

      // jos ei muutoksia, skip
      if (!computeDirty(draft, base)) {
        return { skipped: true };
      }

      const patch = buildPatch ? buildPatch(draft, base) : { ...draft };

      setSaving(true);
      try {
        const out = (await onSave(patch, { draft, base, ...ctx })) || {};
        const savedAt = typeof out.savedAt === "number" ? out.savedAt : Date.now();

        // 1) Päivitä draft jos backend haluaa antaa normalisoidun version
        const nextDraft = out.savedDraft ? { ...(draft || {}), ...out.savedDraft } : draft;

        // 2) Päivitä base: ensisijaisesti mergedBase, muuten nextDraft (+savedAt jos kenttä on)
        const nextBase =
          out.mergedBase
            ? { ...(base || {}), ...out.mergedBase }
            : { ...(nextDraft || {}) };

        // commit
        baseRef.current = { ...nextBase };

        // pidä draft myös ajantasalla
        setDraft(nextDraft);

        return { ok: true, savedAt, patch, out };
      } finally {
        setSaving(false);
      }
    },
    [draft, computeDirty, onSave, buildPatch]
  );

  return {
    draft,
    setDraft, // jos joskus tarvitset suoran asettamisen
    setField,
    setMany,

    saving,
    dirty,

    baseRef, // jos haluat debugata / käyttää id-vertailuun tms
    resetFromEntity,
    syncFromEntityIfSafe,
    commitBase,
    save,
  };
}
